import React, { useState } from 'react';
import { arrayOf, string, func } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { CHECKLIST_STEPS, getProgress, isOnboardingComplete } from './onboardingUtils';
import css from './OnboardingChecklist.module.css';

/**
 * OnboardingChecklist — post-signup onboarding checklist.
 *
 * Shows a panel with steps to complete. Hidden when dismissed or all required steps done.
 *
 * Usage (in LandingPage or TopbarDesktop after login):
 *   {features.onboardingChecklist && !dismissed && !isComplete && (
 *     <OnboardingChecklist completed={user.protectedData.onboarding?.completed || []} />
 *   )}
 *
 * @param {string[]} props.completed — completed step IDs from user.protectedData.onboarding.completed
 * @param {Function} [props.onDismiss]
 */
const OnboardingChecklist = ({ completed, onDismiss, className }) => {
  const [localCompleted, setLocalCompleted] = useState(completed);

  const progress = getProgress(localCompleted);
  const allDone = isOnboardingComplete(localCompleted);

  if (allDone) return null;

  const handleDismiss = async () => {
    try {
      await fetch('/api/onboarding/dismiss', { method: 'POST' });
    } catch (_) {}
    onDismiss && onDismiss();
  };

  const handleStepClick = async (step) => {
    if (localCompleted.includes(step.id)) return;
    try {
      await fetch('/api/onboarding/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stepId: step.id }),
      });
      setLocalCompleted(prev => [...prev, step.id]);
    } catch (_) {}
    if (step.url) window.location.href = step.url;
  };

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.header}>
        <div>
          <h3 className={css.title}><FormattedMessage id="Onboarding.title" /></h3>
          <p className={css.subtitle}>
            <FormattedMessage id="Onboarding.progress" values={{ percent: progress }} />
          </p>
        </div>
        <button type="button" onClick={handleDismiss} className={css.closeBtn}>✕</button>
      </div>

      <div className={css.progressBar}>
        <div className={css.progressFill} style={{ width: `${progress}%` }} />
      </div>

      <div className={css.steps}>
        {CHECKLIST_STEPS.map(step => {
          const done = localCompleted.includes(step.id);
          return (
            <div
              key={step.id}
              className={classNames(css.step, done && css.stepDone)}
              onClick={() => !done && handleStepClick(step)}
              role={done ? 'listitem' : 'button'}
            >
              <span className={css.checkmark}>{done ? '✓' : '○'}</span>
              <div className={css.stepText}>
                <span className={css.stepLabel}>
                  <FormattedMessage id={step.labelKey} />
                  {step.required && <span className={css.required}>*</span>}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

OnboardingChecklist.defaultProps = { onDismiss: null, className: null };
OnboardingChecklist.propTypes = {
  completed: arrayOf(string).isRequired,
  onDismiss: func,
  className: string,
};

export default OnboardingChecklist;
